import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';

// Comparison data for all business structures
const comparisonData = [
  {
    criteria: 'Who is it best for?',
    proprietorship: 'Solo freelancers',
    partnership: '2-3 partners',
    llp: 'Professional firms',
    privateLimited: 'Startups & funded businesses',
    opc: 'Solo founders wanting company status',
  },
  {
    criteria: 'Legal identity',
    proprietorship: 'Same as owner',
    partnership: 'Same as partners',
    llp: 'Separate entity',
    privateLimited: 'Separate entity',
    opc: 'Separate entity',
  },
  {
    criteria: 'Owner liability',
    proprietorship: 'Unlimited',
    partnership: 'Unlimited',
    llp: 'Limited',
    privateLimited: 'Limited',
    opc: 'Limited',
  },
  {
    criteria: 'Number of owners',
    proprietorship: '1',
    partnership: '2-50',
    llp: '2+',
    privateLimited: '2-200',
    opc: '1',
  },
  {
    criteria: 'Compliance level',
    proprietorship: 'Very low',
    partnership: 'Low',
    llp: 'Medium',
    privateLimited: 'High',
    opc: 'Medium',
  },
  {
    criteria: 'Statutory audit',
    proprietorship: 'Not required',
    partnership: 'Not required',
    llp: 'Conditional',
    privateLimited: 'Mandatory',
    opc: 'Mandatory',
  },
  {
    criteria: 'Ease of fundraising',
    proprietorship: 'Not suitable',
    partnership: 'Difficult',
    llp: 'Limited',
    privateLimited: 'Best suited',
    opc: 'Limited',
  },
  {
    criteria: 'Foreign investment',
    proprietorship: false,
    partnership: false,
    llp: true,
    privateLimited: true,
    opc: false,
  },
  {
    criteria: 'Ideal growth stage',
    proprietorship: 'Early / small',
    partnership: 'Small',
    llp: 'Growing',
    privateLimited: 'Scaling',
    opc: 'Early-growth',
  },
  {
    criteria: 'Govt. registration',
    proprietorship: 'Minimal',
    partnership: 'Optional',
    llp: 'Mandatory',
    privateLimited: 'Mandatory',
    opc: 'Mandatory',
  },
];

// Map slug to column key
const slugToColumn = {
  'proprietorship': 'proprietorship',
  'partnership': 'partnership',
  'llp': 'llp',
  'private-limited': 'privateLimited',
  'opc': 'opc',
};

// Column headers with links
const columns = [
  { key: 'proprietorship', label: 'Proprietorship', slug: 'proprietorship' },
  { key: 'partnership', label: 'Partnership', slug: 'partnership' },
  { key: 'llp', label: 'LLP', slug: 'llp' },
  { key: 'privateLimited', label: 'Pvt Ltd', slug: 'private-limited' },
  { key: 'opc', label: 'OPC', slug: 'opc' },
];

const BusinessComparisonTable = ({ currentServiceSlug }) => {
  const currentColumn = slugToColumn[currentServiceSlug];

  const renderCell = (value, isCurrentColumn) => {
    // Handle boolean values (for Foreign investment)
    if (typeof value === 'boolean') {
      return value ? (
        <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
          <Check className="h-3.5 w-3.5" /> Yes
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-slate-400">
          <X className="h-3.5 w-3.5" /> No
        </span>
      );
    }
    return value;
  };

  return (
    <div className="mt-10 mb-6" data-testid="business-comparison-table">
      {/* Heading - centered on desktop only */}
      <h2 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3 lg:text-center">
        Compare business structures
      </h2>
      <p className="text-sm text-slate-500 mb-5 lg:text-center">
        Choose the right entity type for your business needs
      </p>
      
      {/* Scrollable container - opaque background for mobile scroll */}
      <div className="overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0 bg-white relative">
        {/* Desktop: fixed layout with even columns | Mobile: auto layout */}
        <table 
          className="w-full min-w-[640px] border-collapse lg:table-fixed bg-white" 
          data-testid="comparison-table"
        >
          <colgroup className="hidden lg:table-column-group">
            {/* Criteria column: 18% width */}
            <col style={{ width: '18%' }} />
            {/* 5 entity columns: remaining 82% split equally = 16.4% each */}
            <col style={{ width: '16.4%' }} />
            <col style={{ width: '16.4%' }} />
            <col style={{ width: '16.4%' }} />
            <col style={{ width: '16.4%' }} />
            <col style={{ width: '16.4%' }} />
          </colgroup>
          <thead className="relative z-20">
            <tr>
              {/* Criteria header - OPAQUE mint/green background for mobile sticky */}
              <th className="text-left text-xs font-semibold text-slate-700 py-3 px-4 bg-emerald-50 border-b border-emerald-100 sticky left-0 z-30 min-w-[140px] lg:static lg:min-w-0 lg:bg-emerald-50/70">
                Criteria
              </th>
              {columns.map((col) => (
                <th 
                  key={col.key}
                  className={`text-center text-xs font-semibold py-3 px-3 border-b min-w-[100px] lg:min-w-0 ${
                    col.key === currentColumn 
                      ? 'text-blue-700 bg-blue-100 lg:bg-blue-100/80 border-blue-200' 
                      : 'text-slate-600 bg-slate-100 lg:bg-slate-100/80 border-slate-200'
                  }`}
                >
                  {col.key === currentColumn ? (
                    <span className="flex flex-col items-center gap-0.5">
                      <span>{col.label}</span>
                      <span className="text-[9px] font-normal text-blue-500">(viewing)</span>
                    </span>
                  ) : (
                    <Link 
                      to={`/service/${col.slug}`}
                      className="hover:text-blue-600 transition-colors"
                    >
                      {col.label}
                    </Link>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comparisonData.map((row, idx) => (
              <tr key={idx} className="transition-colors hover:bg-slate-50/80">
                {/* Criteria cell - OPAQUE mint/green background for mobile sticky */}
                <td className="text-sm font-medium text-slate-700 py-3.5 px-4 border-b border-emerald-100/80 sticky left-0 z-10 bg-emerald-50 lg:static lg:bg-emerald-50/50 transition-colors group-hover:bg-emerald-100">
                  {row.criteria}
                </td>
                {columns.map((col) => (
                  <td 
                    key={col.key}
                    className={`text-sm text-center py-3.5 px-3 border-b transition-colors ${
                      col.key === currentColumn 
                        ? 'text-slate-800 bg-blue-50 lg:bg-blue-50/50 font-medium border-blue-100/80' 
                        : 'text-slate-600 bg-white border-slate-100'
                    }`}
                  >
                    {renderCell(row[col.key], col.key === currentColumn)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Mobile hint */}
      <p className="text-[10px] text-slate-400 mt-3 lg:hidden text-center">
        Scroll horizontally to see all options
      </p>
    </div>
  );
};

export default BusinessComparisonTable;
